import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * 🎁 Owner-only Loot Table Tuner — edit per-item weights for any festival
 * board's loot table. Persists to mongo and re-applies on startup.
 *
 * Default board: phoenix_summit. Owner can switch via the dropdown.
 */
// 🪦 Feb 2026 owner directive — Phoenix Summit is NO LONGER a selectable
// board here. Arcade / festival content runs only during holidays and
// special events, not publicly 100% of the time. The admin UI must not
// let anyone flip it on by accident. Code defaults still live in
// festival_boards/phoenix_summit.py so a future event re-enable is a
// one-line change there.
const KNOWN_BOARDS = [
  { key: "july_4_2026",    name: "🎆 4th of July (placeholder — no loot table)" },
];

export default function LootTableTuner() {
  const [boardKey, setBoardKey] = useState("july_4_2026");
  const [data, setData] = useState(null);
  const [weights, setWeights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [unauthorized, setUnauthorized] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsWindow, setAnalyticsWindow] = useState("last_7d");
  const [targets, setTargets] = useState({});    // {<name>: pct} for auto-balance
  const [autoBalancing, setAutoBalancing] = useState(false);

  const fetchAnalytics = async (key = boardKey) => {
    try {
      const res = await axios.get(`${API}/festival/owner/loot-analytics?board_key=${key}`,
        { withCredentials: true });
      setAnalytics(res.data);
    } catch (e) {
      if (e?.response?.status !== 403) {
        console.warn("[loot-analytics] fetch failed", e);
      }
    }
  };

  const fetchTable = async (key = boardKey) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/festival/owner/loot-table?board_key=${key}`,
        { withCredentials: true });
      setData(res.data);
      setWeights(res.data.items.map(i => i.weight));
      // Fetch analytics in parallel
      fetchAnalytics(key);
    } catch (e) {
      if (e?.response?.status === 403) setUnauthorized(true);
      else toast.error(e?.response?.data?.detail || "Failed to load loot table");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTable(boardKey); }, [boardKey]);

  const totalWeight = weights.reduce((a, b) => a + Number(b || 0), 0);
  const previewItems = (data?.items || []).map((it, i) => ({
    ...it,
    weight: Number(weights[i] ?? 0),
    rate_pct: totalWeight > 0 ? +((Number(weights[i] ?? 0) / totalWeight) * 100).toFixed(2) : 0,
  }));

  const handleChange = (i, val) => {
    const v = Math.max(0, parseInt(val || "0", 10));
    const next = [...weights];
    next[i] = v;
    setWeights(next);
  };

  const handleSave = async () => {
    if (totalWeight <= 0) {
      toast.error("At least one weight must be > 0.");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/festival/owner/loot-table`,
        { board_key: boardKey, weights: weights.map(w => Number(w || 0)) },
        { withCredentials: true });
      setData(res.data);
      setWeights(res.data.items.map(i => i.weight));
      toast.success("🎁 Loot table saved — live now.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Clear persisted overrides? Code defaults reapply next server restart.")) return;
    try {
      const res = await axios.post(`${API}/festival/owner/loot-table/reset`,
        { board_key: boardKey }, { withCredentials: true });
      setData(res.data);
      setWeights(res.data.items.map(i => i.weight));
      toast.success("Overrides cleared. Defaults reapply on next restart.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed.");
    }
  };

  const handleAutoBalance = async () => {
    // Build targets dict from non-empty target inputs
    const cleanTargets = {};
    for (const [name, val] of Object.entries(targets)) {
      const n = parseFloat(val);
      if (!isNaN(n) && n > 0) cleanTargets[name] = n;
    }
    if (Object.keys(cleanTargets).length === 0) {
      toast.error("Set at least one target % to auto-balance.");
      return;
    }
    const totalTarget = Object.values(cleanTargets).reduce((a, b) => a + b, 0);
    if (totalTarget > 100.5) {
      toast.error(`Targets sum to ${totalTarget.toFixed(2)}% — must be ≤ 100%.`);
      return;
    }
    if (!window.confirm(
      `Auto-balance ${Object.keys(cleanTargets).length} target(s)? ` +
      `Unspecified items keep their relative balance.`
    )) return;
    setAutoBalancing(true);
    try {
      const res = await axios.post(`${API}/festival/owner/loot-auto-balance`,
        { board_key: boardKey, targets: cleanTargets, base_scale: 20 },
        { withCredentials: true });
      setData(res.data);
      setWeights(res.data.items.map(i => i.weight));
      setTargets({});
      toast.success("🎯 Weights auto-balanced and saved live.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auto-balance failed.");
    } finally {
      setAutoBalancing(false);
    }
  };

  if (unauthorized) {
    return (
      <div className="max-w-2xl mx-auto p-8 text-center text-zinc-300"
           data-testid="loot-tuner-unauthorized">
        <div className="text-6xl mb-4">🔒</div>
        <h2 className="text-2xl font-bold">Owner Access Only</h2>
        <p className="text-zinc-500 mt-2">This page is restricted to the Phoenix Council.</p>
      </div>
    );
  }

  if (loading) {
    return <div className="p-8 text-center text-zinc-400" data-testid="loot-tuner-loading">Loading…</div>;
  }

  if (!data?.items?.length) {
    return (
      <div className="max-w-2xl mx-auto p-8 text-center text-zinc-300"
           data-testid="loot-tuner-empty">
        <div className="text-5xl mb-3">🎁</div>
        <h2 className="text-xl font-bold mb-1">No loot table on this board</h2>
        <p className="text-zinc-500 text-sm">Pick a different board from the dropdown.</p>
        <select className="mt-4 bg-zinc-800 text-white px-3 py-2 rounded"
                value={boardKey} onChange={(e) => setBoardKey(e.target.value)}
                data-testid="loot-tuner-board-select">
          {KNOWN_BOARDS.map(b => <option key={b.key} value={b.key}>{b.name}</option>)}
        </select>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6 text-white"
         data-testid="loot-tuner"
         style={{ minHeight: "100vh", background: "linear-gradient(135deg, #0F172A 0%, #1E1B4B 100%)" }}>
      <div className="text-center mb-6">
        <div className="text-5xl mb-2">🎁</div>
        <h1 className="text-3xl font-bold">Loot Table Tuner</h1>
        <p className="text-zinc-400 text-sm mt-1">Owner-only · edits live · persists across restarts</p>
      </div>

      <div className="mb-4 flex items-center gap-3">
        <label className="text-sm text-zinc-400">Board:</label>
        <select className="bg-zinc-800 text-white px-3 py-2 rounded border border-zinc-700"
                value={boardKey} onChange={(e) => setBoardKey(e.target.value)}
                data-testid="loot-tuner-board-select">
          {KNOWN_BOARDS.map(b => <option key={b.key} value={b.key}>{b.name}</option>)}
        </select>
      </div>

      <div className="rounded-lg overflow-hidden border border-zinc-700/70" data-testid="loot-tuner-table">
        <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-zinc-900/80 text-xs uppercase text-zinc-400 tracking-wider font-bold">
          <div className="col-span-1">Icon</div>
          <div className="col-span-4">Item</div>
          <div className="col-span-2 text-right">Weight</div>
          <div className="col-span-2 text-right">Drop Rate</div>
          <div className="col-span-3 text-right">Target % (auto-balance)</div>
        </div>
        {previewItems.map((it, i) => (
          <div key={i}
               className="grid grid-cols-12 gap-2 px-3 py-3 items-center border-t border-zinc-800/60 hover:bg-white/5 transition"
               data-testid={`loot-tuner-row-${i}`}>
            <div className="col-span-1 text-2xl text-center">{it.icon}</div>
            <div className="col-span-4">
              <div className="font-semibold">{it.name}</div>
              <div className="text-[10px] text-zinc-500">
                {Object.entries(it.rewards).map(([k, v]) => (
                  <span key={k} className="mr-2">
                    {k}: {Array.isArray(v) ? JSON.stringify(v) : (typeof v === "object" ? JSON.stringify(v) : v)}
                  </span>
                ))}
              </div>
            </div>
            <div className="col-span-2 text-right">
              <input
                type="number"
                min="0"
                value={weights[i] ?? 0}
                onChange={(e) => handleChange(i, e.target.value)}
                data-testid={`loot-tuner-weight-${i}`}
                className="w-20 bg-zinc-800 text-white text-right px-2 py-1 rounded border border-zinc-700"
              />
            </div>
            <div className="col-span-2 text-right">
              <div className="inline-flex items-center gap-2">
                <div className="h-2 w-20 bg-zinc-800 rounded overflow-hidden">
                  <div className="h-full bg-amber-400"
                       style={{ width: `${Math.min(100, it.rate_pct)}%` }} />
                </div>
                <span className="text-amber-300 font-bold tabular-nums w-14 text-right">
                  {it.rate_pct.toFixed(2)}%
                </span>
              </div>
            </div>
            <div className="col-span-3 text-right">
              <input
                type="number"
                min="0"
                max="100"
                step="0.5"
                placeholder="—"
                value={targets[it.name] ?? ""}
                onChange={(e) => setTargets(t => ({ ...t, [it.name]: e.target.value }))}
                data-testid={`loot-tuner-target-${i}`}
                className="w-24 bg-zinc-800 text-cyan-300 text-right px-2 py-1 rounded border border-cyan-700/50 placeholder-zinc-600"
              />
              <span className="text-cyan-400 ml-1">%</span>
            </div>
          </div>
        ))}
        <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-zinc-900/80 border-t border-zinc-700/70 text-xs">
          <div className="col-span-5 text-zinc-400">Total weight · target sum</div>
          <div className="col-span-2 text-right text-white font-bold tabular-nums">{totalWeight}</div>
          <div className="col-span-2 text-right text-zinc-400">100%</div>
          <div className="col-span-3 text-right text-cyan-300 font-bold tabular-nums">
            {Object.values(targets).reduce((a, v) => a + (parseFloat(v) || 0), 0).toFixed(2)}%
          </div>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-3 justify-center">
        <button onClick={handleSave}
                disabled={saving || totalWeight <= 0}
                data-testid="loot-tuner-save"
                className="px-5 py-2.5 bg-amber-400 text-zinc-900 font-bold rounded disabled:opacity-40 hover:bg-amber-300 transition">
          {saving ? "Saving…" : "💾 Save Weights"}
        </button>
        <button onClick={handleAutoBalance}
                disabled={autoBalancing || Object.values(targets).every(v => !parseFloat(v))}
                data-testid="loot-tuner-auto-balance"
                className="px-5 py-2.5 bg-cyan-500 text-zinc-900 font-bold rounded disabled:opacity-40 hover:bg-cyan-400 transition">
          {autoBalancing ? "Balancing…" : "🎯 Auto-Balance to Targets"}
        </button>
        <button onClick={() => fetchTable()}
                data-testid="loot-tuner-refresh"
                className="px-5 py-2.5 border border-zinc-600 text-zinc-300 rounded hover:bg-white/5 transition">
          🔄 Reload
        </button>
        <button onClick={handleReset}
                data-testid="loot-tuner-reset"
                className="px-5 py-2.5 border border-rose-500 text-rose-300 rounded hover:bg-rose-900/30 transition">
          🗑️ Clear Overrides
        </button>
      </div>

      <p className="text-xs text-zinc-500 mt-4 text-center">
        💡 Weights are relative · drop rate = weight ÷ total. Save commits the
        new weights live; new loot box landings use them immediately.
      </p>

      {/* ── 📊 Drop Analytics Panel ── */}
      {analytics && (
        <div className="mt-8 rounded-lg border border-zinc-700/70 overflow-hidden"
             data-testid="loot-analytics-panel">
          <div className="px-3 py-2 bg-zinc-900/80 flex items-center justify-between">
            <h2 className="text-sm font-bold uppercase tracking-wider text-amber-300">
              📊 Drop Analytics
            </h2>
            <div className="flex gap-1">
              {[
                { id: "today_pt", label: "Today" },
                { id: "last_7d",  label: "7d" },
                { id: "last_30d", label: "30d" },
                { id: "all_time", label: "All" },
              ].map(w => (
                <button key={w.id}
                        onClick={() => setAnalyticsWindow(w.id)}
                        data-testid={`loot-analytics-window-${w.id}`}
                        className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider transition"
                        style={{
                          color: analyticsWindow === w.id ? "#0A0A0A" : "#FBBF24",
                          background: analyticsWindow === w.id ? "#FBBF24" : "transparent",
                          border: "1px solid #FBBF24",
                          borderRadius: 4,
                        }}>
                  {w.label}
                </button>
              ))}
            </div>
          </div>
          {(() => {
            const w = analytics.windows[analyticsWindow] || { total: 0, items: {} };
            if (w.total === 0) {
              return (
                <div className="px-3 py-8 text-center text-zinc-500 text-sm">
                  No drops recorded in this window yet.
                </div>
              );
            }
            // Sort items by expected_pct desc for consistency
            const rows = Object.entries(w.items).map(([name, v]) => {
              const exp = analytics.expected[name] || {};
              return { name, icon: exp.icon, ...v };
            }).sort((a, b) => b.expected_pct - a.expected_pct);
            return (
              <>
                <div className="grid grid-cols-12 gap-2 px-3 py-1.5 bg-zinc-900/40 text-[10px] uppercase text-zinc-500 tracking-wider font-bold">
                  <div className="col-span-1">Icon</div>
                  <div className="col-span-4">Item</div>
                  <div className="col-span-2 text-right">Drops</div>
                  <div className="col-span-2 text-right">Actual</div>
                  <div className="col-span-2 text-right">Expected</div>
                  <div className="col-span-1 text-right">Δ</div>
                </div>
                {rows.map((r) => {
                  const drift = r.diff_pct;
                  const driftColor = Math.abs(drift) < 2 ? "text-zinc-400"
                                    : drift > 0 ? "text-emerald-400" : "text-rose-400";
                  return (
                    <div key={r.name}
                         className="grid grid-cols-12 gap-2 px-3 py-2 items-center border-t border-zinc-800/60 text-xs"
                         data-testid={`loot-analytics-row-${r.name.replace(/\s+/g, '-').toLowerCase()}`}>
                      <div className="col-span-1 text-xl text-center">{r.icon}</div>
                      <div className="col-span-4 text-zinc-200">{r.name}</div>
                      <div className="col-span-2 text-right text-white tabular-nums">{r.count}</div>
                      <div className="col-span-2 text-right text-amber-300 font-bold tabular-nums">
                        {r.actual_pct.toFixed(2)}%
                      </div>
                      <div className="col-span-2 text-right text-zinc-400 tabular-nums">
                        {r.expected_pct.toFixed(2)}%
                      </div>
                      <div className={`col-span-1 text-right font-bold tabular-nums ${driftColor}`}>
                        {drift > 0 ? "+" : ""}{drift.toFixed(1)}
                      </div>
                    </div>
                  );
                })}
                <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-zinc-900/80 border-t border-zinc-700/70 text-[11px]">
                  <div className="col-span-5 text-zinc-400">Total drops</div>
                  <div className="col-span-2 text-right text-white font-bold">{w.total}</div>
                  <div className="col-span-5 text-right text-zinc-500">
                    Δ in green = drops higher than expected · red = lower
                  </div>
                </div>
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
}
